import 'simplebar/src/simplebar.css'
import React, { useRef, useCallback } from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Select, { components as selectComponents } from 'react-select'
import CreatableSelectBase from 'react-select/creatable'
import { withStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import NoSsr from '@material-ui/core/NoSsr'
import TextField from '@material-ui/core/TextField'
import Paper from '@material-ui/core/Paper'
import Chip from '@material-ui/core/Chip'
import MenuItem from '@material-ui/core/MenuItem'
import CancelIcon from '@material-ui/icons/Cancel'
import { emphasize } from '@material-ui/core'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import SimpleBarReact from 'simplebar-react'
import { List } from 'immutable'

const styles = (theme) => ({
  root: {
    flexGrow: 1,
    height: 250,
  },
  input: {
    display: 'flex',
    padding: 0,
    height: 32,
  },
  multiInput: {
    display: 'flex',
    padding: 0,
    height: 44,
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden',
  },
  chip: {
    margin: theme.spacing(0.5, 0.2),
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08
    ),
  },
  noOptionsMessage: {
    padding: theme.spacing(1, 2),
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    zIndex: 1,
    fontSize: 16,
  },
  paper: {
    position: 'absolute',
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing(2),
  },
})

function NoOptionsMessage(props) {
  return (
    <Typography color="textSecondary" className={props.selectProps.classes.noOptionsMessage} {...props.innerProps}>
      {props.children}
    </Typography>
  )
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />
}

function Control(props) {
  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes[_.get(props, 'selectProps.custom.imputClass', 'input')],
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
        ...(_.get(props, 'selectProps.custom.overrideMargin', false)
          ? {
              style: {
                height: '100%',
              },
            }
          : {}),
      }}
      {...props.selectProps.textFieldProps}
    />
  )
}

function Option(props) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      // selected={props.isFocused}
      component="div"
      style={_.compact({
        fontWeight: props.isSelected ? 500 : 400,
        ...props.getStyles('option', props),
        backgroundColor: '#fff',
        color: '#000',
      })}
      {...props.innerProps}>
      {props.children}
    </MenuItem>
  )
}

function Placeholder(props) {
  return (
    <Typography
      color="textSecondary"
      style={props.getStyles('placeholder', props)}
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}>
      {props.children}
    </Typography>
  )
}

function SingleValue(props) {
  return (
    <Typography
      style={{
        // width: '100%',
        marginLeft: 18,
        marginRight: 8,
        ..._.compact(props.getStyles('singleValue', props)),
      }}
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}>
      {props.children}
    </Typography>
  )
}

function ValueContainer(props) {
  return (
    <div
      className={props.selectProps.classes.ValueContainer}
      style={{
        width: '100%',
        height: '100%',
        marginLeft: 24,
        marginRight: 8,
        textOverflow: 'ellipsis',
        height: _.get(props, 'selectProps.custom.sizeContainer', 22),
        wordBreak: 'keep-all',
        ...props.getStyles('singleValue', props),
      }}>
      {props.children}
    </div>
  )
}

function ValueContainerCreated(props) {
  return (
    <div
      className={props.selectProps.classes.valueContainer}
      style={{
        textOverflow: 'ellipsis',
        height: 19,
        wordBreak: 'keep-all',
        padding: '8px 12px',
        color: '#90a4af !important',
      }}>
      {props.children}
    </div>
  )
}

function SingleValueCreated(props) {
  return (
    <div className={props.selectProps.classes.singleValue} {...props.innerProps} style={{ color: '#90a4af' }}>
      {props.children}
    </div>
  )
}

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={clsx(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  )
}

function Menu(props) {
  return (
    <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  )
}

export const components = selectComponents

function SelectControl(props) {
  return <Select components={components} {...props} />
}

export default SelectControl

export const CreatableSelect = CreatableSelectBase

const reactSelectStyles = {
  placeholder: (base) => ({ ...base, left: 13, color: '#90a4af', opacity: 0.5 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  control: (base, state) => ({
    ...base,
    background: state.isDisabled ? '#1a212580' : '#1a2125',
    color: '#90a4af',
    border: 0,
    fontSize: 16,
    padding: '0 !important',
    borderRadius: 0,
    minHeight: 35,
    height: 35,
  }),
  singleValue: (base) => ({
    ...base,
    color: '#90a4af',
    fontSize: 16,
  }),
  input: (base) => ({
    ...base,
    color: '#90a4af',
    fontSize: 16,
  }),
  option: (base) => ({
    ...base,
    color: '#000',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    background: '#1a2125',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#90a4af',
  }),
}
export const getSelectElementStyles = function (styles) {
  const elementStylesSelect = {
    placeholder: (base) => ({
      ...base,
      color: '#f9fafa',
      opacity: 1,
      transform: 'none',
      WebkitTransform: 'none',
      top: '4px',
      ..._.get(styles, 'placeholder', {}),
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
      background: '#263238',
      ..._.get(styles, 'menuPortal', {}),
    }),
    menuList: (base) => ({
      ...base,
      '&::-webkit-scrollbar': {
        width: 4,
        height: 4,
      },
      '&::-webkit-scrollbar-track': {
        borderRadius: 10,
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 10,
        boxShadow: 'inset 0 0 6px rgba(73, 88, 94, 1)',
      },
    }),
    container: (base) => ({
      ...base,
      border: '0px !important',
      outline: 'none !important',
    }),
    control: (base, state) => ({
      ...base,
      background: state.isDisabled ? '#1a212580' : '#49585e',
      borderRadius: '4px',
      color: '#f9fafa',
      fontSize: 16,
      padding: '0 !important',
      minHeight: 32,
      height: 32,
      border: '0px !important',
      boxShadow: 'none !important',
      ..._.get(styles, 'control', {}),
    }),
    singleValue: (base, state) => ({
      ...base,
      color: state.isDisabled ? '#B2B3B3' : '#f9fafa',
      fontSize: 16,
      transform: 'none',
      WebkitTransform: 'none',
      top: '4px',
      ..._.get(styles, 'singleValue', {}),
      '& svg': {
        fill: 'hsl(0,0%,20%)',
      },
    }),
    multiValue: (base) => ({
      ...base,
      color: '#f9fafa',
      fontSize: 16,
      transform: 'none',
      WebkitTransform: 'none',
      top: '4px',
      ..._.get(styles, 'singleValue', {}),
      '& svg': {
        fill: 'hsl(0,0%,20%)',
      },
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      ':hover': {
        backgroundColor: 'none',
      },
    }),
    input: (base) => ({
      ...base,
      color: '#f9fafa',
      fontSize: 16,
      ..._.get(styles, 'input', {}),
    }),
    indicatorSeparator: (base) => ({
      ...base,
      background: '#49585e',
      ..._.get(styles, 'indicatorSeparator', {}),
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isDisabled ? '#B2B3B3' : '#f9fafa',
    }),
    menu: (base) => ({
      ...base,
      background: '#263238',
      ..._.get(styles, 'menu', {}),
    }),
    option: (base, params) => {
      return {
        ...base,
        borderBottom: '1px solid #454c50',
        color: '#80858b',
        background: params.isFocused ? '#707f84 !important' : '#263238 !important',
        ...(params.isFocused ? { color: 'white' } : {}),
        cursor: 'pointer',
        '&:hover': {
          color: 'white',
        },
        ..._.get(styles, 'option', {}),
      }
    },
    indicatorsContainer: (base) => ({
      ...base,
      '& svg': {
        width: '17px !important',
        height: '17px !important',
      },
      marginRight: '-1px',
      ..._.get(styles, 'indicatorsContainer', {}),
    }),
    valueContainer: (base) => ({
      ...base,
      ..._.get(styles, 'valueContainer', {}),
      maxHeight: '100%',
      alignContent: 'center',
    }),
  }
  return elementStylesSelect
}
const toolbarStylesSelect = {
  placeholder: (base) => ({ ...base, left: 13, color: '#f9fafa', opacity: 1 }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  control: (base, state) => ({
    ...base,
    background: state.isDisabled ? '#1a212580' : state.menuIsOpen ? '#49585e' : '#263238',
    border: '1px solid #263238',
    borderRadius: '0px',
    color: '#fff',
    fontSize: 16,
    padding: '0 !important',
    minHeight: 35,
    height: 35,
    fontFamily: 'Lato',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#fff',
    fontSize: 16,
    fontFamily: 'Lato',
  }),
  input: (base) => ({
    ...base,
    color: '#fff',
    fontSize: 16,
    fontFamily: 'Lato',
  }),
  option: (base) => ({
    ...base,
    color: '#000',
    fontFamily: 'Lato',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    background: 'transparent',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 6,
    color: '#fff',
    '&:hover': {
      color: 'white',
    },
  }),
  menu: (base) => ({
    ...base,
    background: '#263238',
    fontFamily: 'Lato',
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 300,
    '::-webkit-scrollbar': {
      width: 4,
      height: 4,
    },
    '::-webkit-scrollbar-track': {
      borderRadius: 10,
    },
    '::-webkit-scrollbar-thumb': {
      borderRadius: 10,
      '-webkit-box-shadow': 'inset 0 0 6px rgba(73, 88, 94, 1)',
    },
  }),
  option: (base, { data }) => ({
    ...base,
    borderBottom: '1px solid #444c50',
    fontFamily: data.value || 'Lato',
    color: '#80858a',
    background: '#263337',
    cursor: 'pointer',
    '&:hover': {
      color: 'white',
    },
  }),
}
const pinkStylesSelect = {
  placeholder: (base) => ({
    ...base,
    color: '#f9fafa',
    opacity: 1,
    transform: 'none',
    WebkitTransform: 'none',
    top: 0,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
    background: '#263238',
  }),
  container: (base) => ({
    ...base,
    border: '0px !important',
    outline: 'none !important',
  }),
  control: (base, state) => ({
    ...base,
    color: '#f9fafa',
    fontSize: 16,
    padding: '0 !important',
    minHeight: 32,
    height: 32,
    border: '0px !important',
    boxShadow: 'none !important',
    backgroundColor: 'transparent',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#f03cc2',
    fontSize: 16,
    transform: 'none',
    WebkitTransform: 'none',
    top: 0,
  }),
  input: (base) => ({
    ...base,
    color: '#f9fafa',
    fontSize: 16,
    height: 32,
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: 'none',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#f9fafa',
  }),
  menu: (base) => ({
    ...base,
    background: '#263238',
  }),
  option: (base) => ({
    ...base,
    borderBottom: '1px solid #454c50',
    color: '#80858b',
    background: '#263238 !important',
    cursor: 'pointer',
    '&:hover': {
      color: 'white',
    },
  }),
  indicatorsContainer: (base) => ({
    ...base,
    margin: '-10px 0 0 -30px',
    color: '#f03cc2',
    '& svg': {
      width: '17px !important',
      height: '17px !important',
      color: '#f03cc2',
      marginTop: -10,
    },
  }),
  valueContainer: (base) => ({
    ...base,
  }),
}

const reactMultipleSelectStyles = {
  placeholder: (base) => ({ ...base, left: 13, color: '#90a4af', opacity: 0.5 }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  control: (base, state) => ({
    ...base,
    background: state.isDisabled ? '#1a212580' : '#1a2125',
    color: '#90a4af',
    border: 0,
    fontSize: 16,
    padding: '0 !important',
    borderRadius: 0,
    minHeight: 35,
    height: 'auto',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#90a4af',
    fontSize: 16,
  }),
  input: (base) => ({
    ...base,
    color: '#90a4af',
    fontSize: 16,
  }),
  option: (base) => ({
    ...base,
    color: '#000',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    background: '#1a2125',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#90a4af',
  }),
}

const reactDefaultSelectStyles = {
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),

  control: (base, state) => ({
    ...base,
    // background: state.isDisabled ? '#1a212580' : '#1a2125',
    // color: '#90a4af',
    border: 0,
    borderBottom: '1px solid #0000006b',
    borderRadius: 0,
    fontSize: 16,
    padding: '0 !important',
    minHeight: 35,
    height: 35,
  }),
  singleValue: (base) => ({
    ...base,
    // color: '#90a4af',
    fontSize: 16,
  }),
  input: (base) => ({
    ...base,
    // color: '#90a4af',
    fontSize: 16,
  }),
}
const getSelectControlStyle = function (isMulti, isElementStyle, isToolbarStyle, styles, isPinkStyle, noOptionFont) {
  if (isPinkStyle) {
    return pinkStylesSelect
  } else if (isElementStyle) {
    return getSelectElementStyles(styles)
  } else if (isToolbarStyle) {
    if (noOptionFont) {
      return {
        ...toolbarStylesSelect,
        option: (base, { data }) => ({
          ...base,
          borderBottom: '1px solid #444c50',
          fontFamily: 'Lato',
          color: '#80858a',
          background: '#263337',
          cursor: 'pointer',
          '&:hover': {
            color: 'white',
          },
        }),
      }
    } else {
      return toolbarStylesSelect
    }
  } else if (isMulti) {
    return reactMultipleSelectStyles
  } else {
    return reactSelectStyles
  }
}

const CustomDropdownIndicator = ({ style, ...rest }) => {
  return (
    <div
      style={{
        marginRight: 13,
        width: 9,
        height: 5,
        cursor: 'pointer',
        background: `url(${process.env.REACT_APP_CDN_URI}/images/TitanWeb_Sprite.png) -532px -17px`,
        ...(rest.selectProps?.menuIsOpen ? { transform: 'rotate(180deg)' } : {}),
      }}
    />
  )
}

const selectEventListener = function (evt) {
  evt.preventDefault()
}

export const PortalCreatableSelect = ({
  styles,
  disabled = false,
  allowCreate = false,
  isDisabled = false,
  isMulti = false,
  isElementStyle = false,
  isToolbarStyle = false,
  isPinkStyle = false,
  noOptionFont = false,
  options,
  ...rest
}) => {
  let eventScroll = useCallback(function (evt) {
    evt.preventDefault()
    evt.stopPropagation()

    if (evt.wheelDeltaY > 0) {
      ref.current.contentWrapperEl.scrollTop -= 40
    } else {
      ref.current.contentWrapperEl.scrollTop += 40
    }
  }, [])
  if (!isElementStyle) {
    const ref = useRef()
    const customMenu = (props) => {
      return (
        <SimpleBarReact
          className="ft--custom"
          style={{ maxHeight: 300, backgroundColor: '#707f84', borderRadius: '1.5px' }}
          ref={ref}>
          {props.children}
        </SimpleBarReact>
      )
    }
    return (
      <CreatableSelectBase
        menuPlacement="auto"
        menuPortalTarget={document.body}
        isDisabled={disabled || isDisabled}
        styles={getSelectControlStyle(isMulti, isElementStyle, isToolbarStyle, styles, isPinkStyle, noOptionFont)}
        isMulti={isMulti}
        theme={(theme) => ({
          ...theme,
          borderRadius: 0,
          colors: {
            ...theme.colors,
            primary25: '#ececec',
            primary: '#aeaeae',
            neutral50: '#90a4af',
          },
        })}
        options={_.isArray(options) || List.isList(options) ? options : []}
        {...rest}
      />
    )
  }

  const ref = useRef()
  const customMenu = (props) => {
    return (
      <SimpleBarReact style={{ maxHeight: 250 }} ref={ref}>
        {props.children}
      </SimpleBarReact>
    )
  }
  let SelectComponent
  if (allowCreate) {
    SelectComponent = CreatableSelectBase
  } else {
    SelectComponent = Select
  }

  return (
    <SelectComponent
      menuPlacement="auto"
      menuPortalTarget={document.body}
      isDisabled={disabled || isDisabled}
      styles={getSelectControlStyle(isMulti, isElementStyle, isToolbarStyle, styles, isPinkStyle)}
      isMulti={isMulti}
      filterOption={(row, search) => {
        if (!search) {
          return true
        }
        search = String(search).toLowerCase()
        let isFiltered = false
        if (row.label) {
          if (String(row.label).toLowerCase().indexOf(search) > -1) {
            isFiltered = true
          }
        }
        if (row.data) {
          _.each(row.data, function (val, key) {
            if (String(val).toLowerCase().indexOf(search) > -1) {
              isFiltered = true
            }
          })
        } else {
          _.each(row, function (val, key) {
            if (String(val).toLowerCase().indexOf(search) > -1) {
              isFiltered = true
            }
          })
        }
        return isFiltered
      }}
      theme={(theme) => ({
        ...theme,
        borderRadius: 0,
        colors: {
          ...theme.colors,
          primary25: '#ececec',
          primary: '#aeaeae',
          neutral50: '#90a4af',
        },
      })}
      options={_.isArray(options) || List.isList(options) ? options : []}
      {...rest}
    />
  )
}

/**
 * For use in props panel
 */
export const PortalSelect = ({ styles, disabled = false, isDisabled = false, ...rest }) => (
  <Select
    menuPlacement="auto"
    menuPortalTarget={document.body}
    isDisabled={disabled || isDisabled}
    styles={reactSelectStyles}
    {...rest}
  />
)

/**
 * For use in dialogs
 */
export const DefaultSelect = ({ styles, disabled = false, isDisabled = false, ...rest }) => (
  <Select
    menuPlacement="auto"
    menuPortalTarget={document.body}
    isDisabled={disabled || isDisabled}
    styles={reactDefaultSelectStyles}
    {...rest}
  />
)
